import { BOOKING_FLOW_QUERY_PARAM_KEYS } from './constants';

/**
 * Selection state carried in the booking-flow query string. `ss`/`sr`/`rs`/`et` are owned and
 * written by the flow; `sistaminuten`/`dealId`/`sb` are pass-throughs the flow reads but never
 * writes (they gate last-minute/deal pricing and the bundle flow — see server/index.js and
 * Bundle.tsx).
 */
export type BookingFlowUrlSelection = {
  /** Comma-separated main service ids. */
  ss?: string;
  /** Selected employee externalId. */
  sr?: string;
  /** Comma-separated picked add-on service ids. */
  rs?: string;
  /**
   * Extra-time answer: '1' yes / '0' no; anything else parses as `undefined` (not answered
   * yet — downstream maps it to `EXTRA_TIME_REQUIREMENT.NotAnsweredYet`).
   */
  et?: '1' | '0';
  sistaminuten?: string;
  dealId?: string;
  sb?: string;
};

/**
 * Patch applied to the flow-owned query params. A `null` value deletes the param; a string value
 * sets it; a key absent from the patch is left untouched. Pass-through params are never part of
 * this type — they can only be carried, never written, by this module's API.
 */
export type BookingFlowSelectionPatch = Partial<Record<'ss' | 'sr' | 'rs' | 'et', string | null>>;

function parseExtraTime(value: string | null): '1' | '0' | undefined {
  return value === '1' || value === '0' ? value : undefined;
}

/** Parses a location search string (with or without the leading '?') into the flow's selection. */
export function parseSelection(search: string): BookingFlowUrlSelection {
  const params = new URLSearchParams(search);

  return {
    ss: params.get(BOOKING_FLOW_QUERY_PARAM_KEYS.SELECTED_SERVICES) ?? undefined,
    sr: params.get(BOOKING_FLOW_QUERY_PARAM_KEYS.SELECTED_RESOURCE) ?? undefined,
    rs: params.get(BOOKING_FLOW_QUERY_PARAM_KEYS.RECOMMENDED_SERVICES) ?? undefined,
    et: parseExtraTime(params.get(BOOKING_FLOW_QUERY_PARAM_KEYS.EXTRA_TIME)),
    sistaminuten: params.get('sistaminuten') ?? undefined,
    dealId: params.get('dealId') ?? undefined,
    sb: params.get('sb') ?? undefined,
  };
}

/**
 * Applies a patch to `currentSearch`; every param not keyed in `patch` — pass-throughs and
 * unrecognised params alike — is preserved untouched. Returns the `location.search` shape:
 * leading '?', or '' when empty.
 */
export function serialiseSelection(currentSearch: string, patch: BookingFlowSelectionPatch): string {
  const params = new URLSearchParams(currentSearch);

  (Object.keys(patch) as Array<keyof BookingFlowSelectionPatch>).forEach((key) => {
    const value = patch[key];
    if (value === null) {
      params.delete(key);
    } else if (value !== undefined) {
      params.set(key, value);
    }
  });

  const serialised = params.toString();
  return serialised ? `?${serialised}` : '';
}

/**
 * The post-pick `service_ids` for tracking: the hydrated selection (`book.services`, which
 * also carries the materialised extra-time add-ons the URL only implies via `et`), plus any
 * `ss`/`rs` id the live URL already holds that hydration has not caught up with — a pick a
 * moment ago — then the new pick. The store alone would report a rapid second pick without
 * the first.
 */
export function nextServiceIds(
  hydrated: readonly { id?: number | null }[] | undefined,
  liveSearch: string,
  serviceId: number,
): string[] {
  const live = parseSelection(liveSearch);
  const liveIds = [live.ss, live.rs]
    .flatMap((ids) => ids?.split(',') ?? [])
    .map((id) => String(parseInt(id, 10)))
    .filter((id) => id !== 'NaN');
  const hydratedIds = (hydrated ?? []).flatMap((service) =>
    typeof service.id === 'number' ? [String(service.id)] : [],
  );
  const ids = [...hydratedIds, ...liveIds, String(serviceId)];
  return ids.filter((id, index) => ids.indexOf(id) === index);
}

/**
 * The picked `ss`/`rs` ids as the readers resolve them (parseInt, NaN dropped), so append and
 * remove operate on what hydration actually matched — a raw '04' renders a row whose remove
 * filter could never hit it otherwise. Pages read `useLocation().search` to render and
 * `history.location.search` at click time: a tap before the re-render must see the ids the
 * previous tap already pushed, or it appends and reports the same pick twice.
 */
export function readPickedIds(search: string): { mainServiceIds: string[]; addOnIds: string[] } {
  const selection = parseSelection(search);
  const normalise = (ids?: string) =>
    (ids?.split(',') ?? []).map((id) => String(parseInt(id, 10))).filter((id) => id !== 'NaN');
  return { mainServiceIds: normalise(selection.ss), addOnIds: normalise(selection.rs) };
}
